import { FC, ChangeEvent } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';
import { trackUserInteractionOnce } from '../helpers/checkoutTracking';
import { ACTIONS, PANES } from '../constants/checkoutConstants';
import { isAdding, isEditing } from '../reducers/checkoutShippingReducer';
import { useCheckoutSelector } from '../reducers/root';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { CheckoutAddressView } from '../shared/CheckoutAddressView';
import { Radio } from 'dibs-elements/exports/Radio';

import styles from './styles/CheckoutShippingAddressList.scss';
import CheckoutShippingForm from './CheckoutShippingForm';
import { CheckoutActions } from '../shared/CheckoutActions';

import type { CheckoutShippingAddressList_viewer$key } from './__generated__/CheckoutShippingAddressList_viewer.graphql';
import type { CheckoutShippingAddressList_buyer$key } from './__generated__/CheckoutShippingAddressList_buyer.graphql';

type Props = {
    viewer: CheckoutShippingAddressList_viewer$key;
    buyer: CheckoutShippingAddressList_buyer$key;
    actions: {
        onDelete: (addressId: string) => void;
        onEdit: (addressId: string, isEditingPartial?: boolean) => void;
        onCancel: () => void;
    };
    isComputing: boolean;
    onSelect: (
        event: ChangeEvent<HTMLInputElement>,
        addressId: string | null,
        isSelected: boolean
    ) => void;
    onChange: () => void;
    onBlur: () => void;
    onSubmit: () => void;
};

const CheckoutShippingAddressList: FC<Props> = props => {
    const {
        viewer: viewerRef,
        buyer: buyerRef,
        actions,
        isComputing,
        onSelect,
        onChange,
        onBlur,
        onSubmit,
    } = props;

    const isAddingShipping = useCheckoutSelector(state => isAdding(state.shipping));
    const isEditingShipping = useCheckoutSelector(state => isEditing(state.shipping));
    const shippingAddressId = useCheckoutSelector(state => state.transaction.shippingAddressId);

    const viewer = useFragment(
        graphql`
            fragment CheckoutShippingAddressList_viewer on Viewer {
                ...CheckoutShippingForm_viewer
            }
        `,
        viewerRef
    );
    const buyer = useFragment(
        graphql`
            fragment CheckoutShippingAddressList_buyer on User {
                ...CheckoutShippingForm_buyer
                addressBook {
                    personalAddressId
                    addressList {
                        ...CheckoutAddressView_address
                        serviceId
                        validation {
                            buyerShipping {
                                valid
                            }
                        }
                    }
                }
            }
        `,
        buyerRef
    );

    const { addressBook } = buyer || {};
    const { addressList, personalAddressId } = addressBook || {};
    const addresses = (addressList || []).filter(a => {
        const isValid = a?.validation?.buyerShipping?.valid;
        return isValid || a?.serviceId === personalAddressId?.toString();
    });

    return (
        <div className="fs-block">
            {addresses.map(address => {
                const serviceId = address?.serviceId;
                const isSelected = !isAddingShipping && serviceId === shippingAddressId;
                const isEditingCurrentAddress = isSelected && isEditingShipping;

                const deleteAction = { [ACTIONS.ACTION_DELETE]: actions.onDelete };
                const isAddressValid = address?.validation?.buyerShipping?.valid;
                const cancelAction = !isAddressValid
                    ? {}
                    : { [ACTIONS.ACTION_CANCEL]: actions.onCancel };

                let label;

                if (isEditingCurrentAddress) {
                    label = (
                        <div className={styles.label}>
                            <span>
                                <FormattedMessage
                                    id="checkout.CheckoutShippingAddressList.editAddress"
                                    defaultMessage="Edit Address"
                                />
                            </span>
                            <CheckoutActions
                                type={PANES.PANE_SHIPPING}
                                serviceId={serviceId || ''}
                                actions={{
                                    ...cancelAction,
                                    ...(isComputing ? null : deleteAction),
                                }}
                            />
                        </div>
                    );
                } else {
                    label = (
                        <div className={styles.label}>
                            {address && <CheckoutAddressView address={address} />}
                            <CheckoutActions
                                type={PANES.PANE_SHIPPING}
                                serviceId={serviceId || ''}
                                actions={{
                                    [ACTIONS.ACTION_EDIT]: actions.onEdit,
                                    ...deleteAction,
                                }}
                            />
                        </div>
                    );
                }

                return (
                    <div
                        key={serviceId}
                        className={classnames(styles.radioContainer, {
                            [styles.clickable]: !isComputing,
                        })}
                        data-tn="checkout-row-container"
                    >
                        <Radio
                            label={label}
                            name={PANES.PANE_SHIPPING}
                            value={serviceId || ''}
                            checked={isSelected}
                            disabled={isComputing}
                            dataTn={[PANES.PANE_SHIPPING, serviceId, 'wrapper'].join('-')}
                            onChange={(value, event) => {
                                trackUserInteractionOnce();
                                if (isComputing) {
                                    return null;
                                }
                                return isEditingCurrentAddress
                                    ? onSelect(event, null, isSelected)
                                    : onSelect(event, value, isSelected);
                            }}
                        />
                        {isEditingCurrentAddress && (
                            <CheckoutShippingForm
                                {...{
                                    viewer,
                                    buyer,
                                    isComputing,
                                    onChange,
                                    onBlur,
                                    onSubmit,
                                }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CheckoutShippingAddressList;
